import { useCallback, useContext } from 'react';
import useFavoritesList from '@smart-tv/hooks/use-favorites-lists';
import useQuery from '@smart-tv/hooks/use-query';
import getEpgDataForChannels from '@smart-tv/utils/kmm/getEpgDataForChannels';
import {
  networkError,
  BaseError,
  ERROR_CODES
} from '@smart-tv/contexts/error-context/error-context';
import { ContentType } from '@smart-tv/typings/content';
import TvGuideContext, { TvGuideQueryParams } from './tv-guide-context';
import { createEmptyPrograms } from './tv-guide-create-empty-programs';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { KMMError } from '@norlys-a-s/norlys-play-kmm';
import { DEFAULT_FAVORITES_LIST } from '@smart-tv/contexts/favorites-lists-context/favorites-lists-context';

const useGetChannels = () => {
  const { date } = useQuery<TvGuideQueryParams>();
  const { channels, setChannels, setSelectedDate, setIsChannelsLoading } =
    useContext(TvGuideContext);
  const { selectedFavoritesList, onSelectFavoritesList } = useFavoritesList();

  const { setIsOffline } = useNetwork();

  const updateChannelsForNewFavorite = useCallback(
    (addingFavorite: boolean, contentId: number, contentType: ContentType) => {
      //only update channels if the user is currently on the TvGuide page
      if (window.location.href.indexOf('tv-guide') === -1) return;
      if (!channels) return;

      const identifier = contentType === 'MOVIES' ? 'programId' : 'seriesId';

      const updatedChannels = channels.map((channel) => {
        const updatedPrograms = channel.programs.map((program) => {
          return program[identifier] === contentId
            ? { ...program, isFavorite: addingFavorite }
            : program;
        });
        return { ...channel, programs: updatedPrograms };
      });

      setChannels(updatedChannels);
    },
    [setChannels, channels]
  );

  const getChannels = useCallback(async () => {
    if (!selectedFavoritesList) return;

    setIsChannelsLoading(true);

    try {
      const filteredDate = date ? new Date(date) : new Date();
      setSelectedDate({ date: filteredDate, isToday: !date });
      const response = await getEpgDataForChannels(filteredDate, selectedFavoritesList.id);
      setChannels(createEmptyPrograms(response.channels));
    } catch (error) {
      if (
        error instanceof KMMError.ApiError &&
        parseInt(error.code, 10) === ERROR_CODES.MISSING_FAVORITES_LIST
      ) {
        await onSelectFavoritesList(DEFAULT_FAVORITES_LIST);
      }
      if (error === networkError || (error as BaseError).offline) {
        setIsOffline(true);
      }
    } finally {
      setIsChannelsLoading(false);
    }
  }, [
    selectedFavoritesList,
    setIsChannelsLoading,
    date,
    setChannels,
    setSelectedDate,
    setIsOffline,
    onSelectFavoritesList
  ]);

  return {
    getChannels,
    selectedFavoritesList,
    date,
    updateChannelsForNewFavorite
  };
};

export default useGetChannels;
